<template>
  <b-container fluid style="max-width:1200px; margin-bottom: 3em;" >
    <template v-if="!restrictedBrand">
      <!-- Brand, Order, Actions -->
      <ulu-inquiry-header
        :inquiry="inquiry"
        :edit="edit"
        :saveNewInquiryGroup="saveNewInquiryGroup"
        :addNewInquiryGroup="addNewInquiryGroup"
        :reload="reload"
        :setShowConfirmModal="setShowConfirmModal"
        :orderExists="orderExists"
      />
      <hr />
      <!-- Cards with details -->
      <ulu-inquiry-details
        :inquiry="inquiry"
        :reload="reload"
        :orderExists="orderExists"
      />
      <!-- Add inquiry -->
      <b-row style="margin-top: 1em" v-if="edit">
        <b-col>
          <b-card bg-variant="light" border-variant="warning">
            <b-row>
              <b-col>
                <h4 class="text-secondary">
                  <i>{{ $t("globals.new_inquiry_group") }}</i>
                </h4>
              </b-col>
            </b-row>
            <hr />
            <b-row style="margin-top: 1em">
              <b-col>
                <h5 class="text-info">{{ $t("globals.duration") }}:</h5>
                <b-form-select
                  style="max-width: 250px"
                  v-model="newInquiryGroup.contract_duration"
                  :options="durationsOptions"
                />
              </b-col>
              <b-col>
                <h5 class="text-info">{{ $t("globals.payment_term") }}:</h5>
                <b-form-select
                  style="max-width: 250px"
                  v-model="newInquiryGroup.payment_term"
                  :options="paymentsOptions"
                />
              </b-col>
            </b-row>
            <!-- Items table -->
            <b-row style="margin-top: 2em">
              <b-col>
                <b-table
                  small
                  bordered
                  hover
                  :items="newInquiryGroup.items"
                  :fields="fields"
                  @row-clicked="itemClicked"
                >
                  <template v-slot:cell(price_one_time)="row">
                    {{
                      row.item.price_one_time === 0
                        ? ""
                        : currencyFormat(
                            row.item.price_one_time,
                            inquiry.currency
                          )
                    }}
                  </template>
                  <template v-slot:cell(price_per_month)="row">
                    {{
                      row.item.price_per_month === 0
                        ? ""
                        : currencyFormat(
                            row.item.price_per_month,
                            inquiry.currency
                          )
                    }}
                  </template>
                  <template v-slot:cell(discount)="row">
                    {{ currencyFormat(row.item.discount, inquiry.currency) }}
                  </template>
                  <template v-slot:cell(vat)="row">
                    {{ row.item.vat.toString() + "%" }}
                  </template>
                  <template v-slot:cell(vat_price)="row">
                    {{
                      currencyFormat(
                        rowItemTotalVatOnly(
                          row.item.price_one_time,
                          row.item.price_per_month,
                          row.item.quantity,
                          newInquiryGroup.contract_duration,
                          row.item.vat,
                          row.item.discount
                        ),
                        inquiry.currency
                      )
                    }}
                  </template>
                  <template v-slot:cell(total_no_vat)="row">
                    {{
                      currencyFormat(
                        rowItemTotalWithoutVat(
                          row.item.price_one_time,
                          row.item.price_per_month,
                          row.item.quantity,
                          row.item.discount,
                          newInquiryGroup.contract_duration
                        ),
                        inquiry.currency
                      )
                    }}
                  </template>
                  <template v-slot:cell(total)="row">
                    {{
                      currencyFormat(
                        rowItemTotal(
                          row.item.price_one_time,
                          row.item.price_per_month,
                          row.item.quantity,
                          row.item.vat,
                          row.item.discount,
                          newInquiryGroup.contract_duration
                        ),
                        inquiry.currency
                      )
                    }}
                  </template>
                  <template v-slot:row-details="row">
                    <b-card>
                      <b-row>
                        <b-col cols="3">
                          <span>{{ $t("globals.item") }}:</span>
                          <b-input
                            size="sm"
                            disabled
                            v-model="row.item.item_name"
                          >
                          </b-input>
                        </b-col>
                        <b-col>
                          <span>{{ $t("globals.price_once") }}:</span>
                          <b-input
                            v-model.number="row.item.price_one_time"
                            type="number"
                            size="sm"
                          >
                          </b-input>
                        </b-col>
                        <b-col>
                          <span>{{ $t("globals.price_monthly") }}:</span>
                          <b-input
                            v-model.number="row.item.price_per_month"
                            type="number"
                            size="sm"
                          >
                          </b-input>
                        </b-col>
                        <b-col>
                          <span>{{ $t("globals.quantity") }}:</span>
                          <b-input
                            v-model.number="row.item.quantity"
                            min="0"
                            type="number"
                            size="sm"
                          >
                          </b-input>
                        </b-col>
                        <b-col>
                          <span>{{ $t("globals.discount_per_item") }}:</span>
                          <b-input
                            v-model.number="row.item.discount"
                            min="0"
                            type="number"
                            size="sm"
                          >
                          </b-input>
                        </b-col>
                        <b-col>
                          <span>{{ $t("globals.vat") }}:</span>
                          <b-input-group append="%" size="sm">
                            <b-input
                              v-model.number="row.item.vat"
                              min="0"
                              type="number"
                              size="sm"
                            >
                            </b-input>
                          </b-input-group>
                        </b-col>
                        <b-col>
                          <b-btn
                            size="sm"
                            style="float: right; margin: 0.3em;"
                            @click="deleteItem(row.item.id)"
                            variant="danger"
                          >
                            <icon name="trash-alt"></icon>
                          </b-btn>
                          <b-btn
                            size="sm"
                            style="float: right; margin: 0.3em;"
                            @click.stop="row.item._showDetails = false"
                            variant="success"
                          >
                            <icon name="save"></icon>
                          </b-btn>
                        </b-col>
                      </b-row>
                    </b-card>
                  </template>
                </b-table>
              </b-col>
            </b-row>
            <!-- Add item -->
            <b-row>
              <b-col>
                <div v-if="!isDurationSet">
                  <b-btn-group
                    v-b-tooltip.hover.bottom
                    title="Select duration first!"
                  >
                    <b-btn @click="addItem" variant="info" disabled>
                      <icon name="plus"></icon>{{ $t("globals.add_item") }}
                    </b-btn>
                  </b-btn-group>
                </div>
                <div v-else>
                  <b-btn @click="addItem" variant="info">
                    <icon name="plus"></icon>{{ $t("globals.add_item") }}
                  </b-btn>
                </div>
              </b-col>
            </b-row>
            <hr />
            <!-- Notes -->
            <b-row>
              <b-col>
                <span>{{ $t("globals.notes") }}:</span>
                <b-form-textarea
                  v-model="newInquiryGroup.notes"
                  placeholder="Enter notes"
                  rows="3"
                >
                </b-form-textarea>
              </b-col>
            </b-row>
            <!-- New item modal -->
            <ulu-item-modal :saveItem="saveItem" :listOfPrices="priceList" />
          </b-card>
        </b-col>
      </b-row>
      <!-- Tabs view -->
      <b-row style="margin-top: 1em">
        <b-col>
          <b-card bg-variant="light">
            <b-tabs>
              <b-tab
                v-for="tab in inquiry.inquiry_group"
                :key="tab.id"
                :title="tab.id"
              >
                <!-- Duration, payment term -->
                <b-row style="margin-top: 2em">
                  <b-col cols="3">
                    <h5 class="text-info">{{ $t("globals.duration") }}:</h5>
                    <b-input
                      :placeholder="tab.contract_duration + ' months'"
                      disabled
                    />
                  </b-col>
                  <b-col cols="3">
                    <h5 class="text-info">{{ $t("globals.payment_term") }}:</h5>
                    <b-input v-model="tab.payment_term_name" disabled />
                  </b-col>
                  <b-col>
                    <b-btn
                      class="float-right m-1"
                      variant="info"
                      @click="showPdf(tab.id)"
                    >
                      <v-icon name="file-pdf"></v-icon>
                    </b-btn>
                    <b-btn
                      v-if="docusignSendEnabled"
                      class="float-right m-1"
                      variant="info"
                      @click="sendDocusign(tab.id)"
                    >
                      {{ $t("globals.send_with_docusign") }}
                    </b-btn>
                  </b-col>
                </b-row>
                <!-- Items table -->
                <b-row style="margin-top: 2em">
                  <b-col>
                    <b-table small bordered :items="tab.items" :fields="fields">
                      <template v-slot:cell(price_one_time)="row">
                        {{
                          row.item.price_one_time === 0
                            ? ""
                            : currencyFormat(
                                row.item.price_one_time,
                                inquiry.currency
                              )
                        }}
                      </template>
                      <template v-slot:cell(price_per_month)="row">
                        {{
                          row.item.price_per_month === 0
                            ? ""
                            : currencyFormat(
                                row.item.price_per_month,
                                inquiry.currency
                              )
                        }}
                      </template>
                      <template v-slot:cell(discount)="row">
                        {{ currencyFormat(row.item.discount, inquiry.currency) }}
                      </template>
                      <template v-slot:cell(vat)="row">
                        {{ row.item.vat.toString() + "%" }}
                      </template>
                      <template v-slot:cell(vat_price)="row">
                        {{
                          currencyFormat(
                            rowItemTotalVatOnly(
                              row.item.price_one_time,
                              row.item.price_per_month,
                              row.item.quantity,
                              tab.contract_duration,
                              row.item.vat,
                              row.item.discount
                            ),
                            inquiry.currency
                          )
                        }}
                      </template>
                      <template v-slot:cell(total_no_vat)="row">
                        {{
                          currencyFormat(
                            rowItemTotalWithoutVat(
                              row.item.price_one_time,
                              row.item.price_per_month,
                              row.item.quantity,
                              row.item.discount,
                              tab.contract_duration
                            ),
                            inquiry.currency
                          )
                        }}
                      </template>
                      <template v-slot:cell(total)="row">
                        {{
                          currencyFormat(
                            rowItemTotal(
                              row.item.price_one_time,
                              row.item.price_per_month,
                              row.item.quantity,
                              row.item.vat,
                              row.item.discount,
                              tab.contract_duration
                            ),
                            inquiry.currency
                          )
                        }}
                      </template>
                    </b-table>
                  </b-col>
                </b-row>
                <!-- Total line -->
                <ulu-grand-total
                  style="margin-top: 2em"
                  :currency="inquiry.currency"
                  :showPayments="showPayments(tab)"
                  :subTotal="subTotal(tab)"
                  :vat="vat(tab)"
                  :grandTotal="grandTotal(tab)"
                  :showVat="true"
                  :payments="payments(tab)"
                  :oneTime="oneTime(tab)"
                  :onePayment="onePayment(tab)"
                />
                <b-row>
                  <b-col>
                    <h5 class="text-info">{{ $t("globals.notes") }}:</h5>
                    <b-form-textarea v-model="tab.notes" rows="3" disabled>
                    </b-form-textarea>
                  </b-col>
                </b-row>
                <!-- Settings table -->
                <ulu-settings-table :settings="tab.settings" />
              </b-tab>
            </b-tabs>
          </b-card>
        </b-col>
      </b-row>
      <!-- Make an order modal -->
      <ulu-inquiry-confirm-modal
        :makeOrder="makeOrder"
        :showModal="showModal"
        :inquiryOptions="options"
        :setShowConfirmModal="setShowConfirmModal"
        :setSelectedInquiryGroup="setSelectedInquiryGroup"
        :setSettings="setSettings"
      />
    </template>
  </b-container>
</template>

<script>
import InquiryGroupItem from "@/models/inquiry/inquiry_group_item";
import { mapActions, mapGetters, mapMutations } from "vuex";
import systemRestriction from "@/mixins/systemRestriction";
import {
  currencyFormat,
  priceWithVat,
  rowItemTotal,
  rowItemTotalWithoutVat,
  rowItemTotalVatOnly
} from "@/helpers/price";
import "vue-awesome/icons/save";
import "vue-awesome/icons/plus";

export default {
  mixins: [systemRestriction],
  systemRestrictionReload: "fetchInquiry",
  components: {
    Icon: () => import("vue-awesome/components/Icon.vue"),
    "ulu-inquiry-details": () =>
      import("@/components/inquiries/inquiry/inquiry_details/InquiryDetails"),
    "ulu-inquiry-header": () =>
      import("@/components/inquiries/inquiry/InquiryHeader"),
    "ulu-inquiry-confirm-modal": () =>
      import("@/components/inquiries/inquiry/InquiryConfirmModal"),
    "ulu-item-modal": () =>
      import("@/components/inquiries/new_inquiry/inquiries/ItemModal"),
    "ulu-grand-total": () =>
      import("@/components/inquiries/new_inquiry/inquiries/GrandTotal"),
    "ulu-settings-table": () => import("@/components/common/SettingsTable")
  },
  data() {
    return {
      priceList: [],
      durationsOptions: [
        { value: null, text: "Select" },
        { value: 0, text: "Non applicable" },
        { value: 12, text: "12 months" },
        { value: 24, text: "24 months" },
        { value: 36, text: "36 months" },
        { value: 48, text: "48 months" },
        { value: 60, text: "60 months" }
      ],
      paymentsOptions: [
        { value: null, text: "Select payment term" },
        { value: 0, text: "One-off" },
        { value: 1, text: "Monthly" },
        { value: 3, text: "Quarterly" },
        { value: 6, text: "Half year" },
        { value: 12, text: "Yearly" }
      ],
      edit: false,
      selectedGroup: null,
      orderExists: false,
      selectedSettings: [],
      inquiry: {
        inquiry_no: 0,
        agent: {
          full_name: ""
        },
        created_at: "",
        contract_start_on: "",
        status: "",
        customer: {
          company_name: "",
          company_country: "",
          isComplete: false
        },
        company: {
          id: 0,
          name: "",
        }
      },
      showModal: false,
      fields: [
        {
          key: "item_name",
          label: this.$t("globals.item")
        },
        {
          key: "price_one_time",
          label: this.$t("globals.price_once")
        },
        {
          key: "price_per_month",
          label: this.$t("globals.price_monthly")
        },
        {
          key: "quantity",
          label: this.$t("globals.quantity")
        },
        {
          key: "discount",
          label: this.$t("globals.discount_per_item")
        },
        {
          key: "vat",
          label: this.$t("globals.vat")
        },
        {
          key: "vat_price",
          label: this.$t("globals.vat_price"),
          class: "text-right"
        },
        {
          key: "total_no_vat",
          label: this.$t("globals.value_no_vat"),
          class: "text-right"
        },
        {
          key: "total",
          label: this.$t("globals.total"),
          class: "text-right"
        }
      ],
      options: [{ text: "", value: 0 }],
      newInquiryGroup: {}
    };
  },
  methods: {
    ...mapActions("orders", ["saveNewOrder"]),
    ...mapActions("axios_mod", ["simpleMutation", "simpleFetch"]),
    ...mapMutations(["setShowItemModal", "setShowLoadingModal"]),
    currencyFormat,
    priceWithVat,
    rowItemTotal,
    rowItemTotalWithoutVat,
    rowItemTotalVatOnly,
    setSettings(value) {
      this.selectedSettings = value;
    },
    setShowConfirmModal(value) {
      this.showModal = value;
    },
    setSelectedInquiryGroup(value) {
      this.selectedGroup = value;
    },
    saveNewInquiryGroup() {
      this.edit = false;
      this.setShowLoadingModal(true);

      let inputInquiryGroup = {};
      inputInquiryGroup.contract_duration = this.newInquiryGroup.contract_duration;
      inputInquiryGroup.notes =
        this.newInquiryGroup.notes === null ? "" : this.newInquiryGroup.notes;
      inputInquiryGroup.payment_term = this.newInquiryGroup.payment_term;
      inputInquiryGroup.items = [];

      this.newInquiryGroup.items.forEach(item => {
        delete item.id;
        delete item._showDetails;
        delete item.price_one_time_with_vat;
        delete item.price_per_month_with_vat;
        inputInquiryGroup.items.push(item);
      });

      let data = {
        query: `
          mutation InquiryGroupAdd($inquiry_id: Int!, $inquiry_group: InquiryGroupInput){
            res: InquiryGroupAdd(inquiry_id: $inquiry_id, inquiry_group: $inquiry_group)
          }
        `,
        variables: {
          inquiry_id: parseInt(this.$route.params.id),
          inquiry_group: inputInquiryGroup
        }
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.fetchInquiry();
      });
    },
    fetchPriceList() {
      const data = {
        query: `
          query  {
            priceList (
              brand: "${this.getUserSystem.toLowerCase()}",
              currency: "${this.inquiry.currency}",
              contract_duration: ${this.newInquiryGroup.contract_duration}
            ) {
              item_name
              item_type
              fleet_size
              price_one_time
              price_per_month
              kpi_type
              exact_code_id
              billing_item_id: id
              vat
              is_device
              displayed
            }
          }
        `
      }
      
      this.setShowLoadingModal(true);
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res) {
            this.priceList = res.priceList;
          }
        })
        .finally(() => { this.setShowLoadingModal(false) });
    },
    itemClicked(item) {
      item._showDetails = true;
      this.newInquiryGroup.items.forEach(groupItem => {
        if (item.id == groupItem.id) return;
        groupItem._showDetails = false;
      });
    },
    deleteItem(id) {
      this.newInquiryGroup.items = this.newInquiryGroup.items.filter(
        item => item.id !== id
      );
    },
    addItem() {
      this.setShowItemModal(true);
    },
    saveItem(item) {
      let newItem = new InquiryGroupItem();
      newItem.id = item.id;
      newItem.item_name = item.name;
      newItem.item_type = item.type;
      newItem.price_one_time = item.priceOneTime;
      newItem.price_per_month = item.priceMonthly;
      newItem.discount = item.discount;
      newItem.quantity = item.quantity;
      newItem.vat = item.vat;
      newItem.kpi_type = item.kpi_type;
      newItem.exact_code_id = item.exact_code_id;
      newItem.billing_item_id = item.billing_item_id;
      newItem.is_device = item.is_device;
      newItem._showDetails = false;

      this.newInquiryGroup.items.push(newItem);

      this.setShowItemModal(false);
    },
    addNewInquiryGroup() {
      this.edit = true;
      let groupCopy = JSON.parse(JSON.stringify(this.inquiry.inquiry_group[0]));
      groupCopy.items.forEach(item => {
        item._showDetails = false;
      });
      this.newInquiryGroup = groupCopy;
      this.fetchPriceList();
    },
    reload() {
      this.edit = false;
      this.newInquiryGroup = {};
      this.fetchInquiry();
    },
    makeOrder() {
      let newOrderData = {
        customer_id: parseInt(this.inquiry.customer.id),
        inquiry_id: parseInt(this.$route.params.id),
        inquiry_group_id: parseInt(this.selectedGroup),
        settings: this.selectedSettings
      };
      this.saveNewOrder({ newOrderData });
    },
    oneTime(tab) {
      let tempOneTime = 0;
      tab.items.forEach(item => {
        if (item.price_one_time > 0) {
          tempOneTime += (item.price_one_time - item.discount) * item.quantity;
        }
      });
      return tempOneTime;
    },
    vat(tab) {
      let subtotalvat = 0;
      tab.items.forEach(item => {
        subtotalvat += this.rowItemTotalVatOnly(
          item.price_one_time,
          item.price_per_month,
          item.quantity,
          tab.contract_duration,
          item.vat,
          item.discount
        );
      });
      return subtotalvat;
    },
    payments(tab) {
      let temp = tab.contract_duration / tab.payment_term;
      if (temp === Infinity) {
        return 1;
      } else {
        return temp;
      }
    },
    onePayment(tab) {
      let monthlyTotal = 0;
      tab.items.forEach(item => {
        if (item.price_per_month > 0) {
          monthlyTotal +=
            (item.price_per_month - item.discount) * item.quantity;
        }
      });

      if (tab.payment_term == 0 && tab.contract_duration == 0) {
        return monthlyTotal;
      } else {
        return (monthlyTotal * tab.contract_duration) / this.payments(tab);
      }
    },
    grandTotal(tab) {
      if (this.payments(tab) > 0) {
        return this.oneTime(tab) + this.payments(tab) * this.onePayment(tab);
      } else {
        return this.oneTime(tab);
      }
    },
    subTotal(tab) {
      return this.oneTime(tab) + this.onePayment(tab);
    },
    fetchInquiry() {
      const data = {
        query: `
          query  {
            inquiry (id: ${parseInt(this.$route.params.id)}) {
              agent {
                full_name
              }
              created_at
              inquiry_no
              currency
              payment_type
              brand
              status
              contract_start_on
              docusign_sent
              customer {
                id
                company_country
                company_name
                isComplete
              }
              company{
                id
                name
              }
              inquiry_group {
                id
                contract_duration
                settings {
                  key
                  value
                }
                notes
                payment_term_name
                total_price_per_month_for_contract_duration
                total_price_one_time
                total_price_per_month
                total_price_per_month_with_discount
                total_price_one_time_with_discount
                total_price_one_time_with_vat
                total_price_per_month_with_vat
                payment_term
                items {
                  id
                  item_name
                  item_type
                  kpi_type
                  price_one_time
                  price_per_month
                  price_one_time_with_vat
                  price_per_month_with_vat
                  discount
                  quantity
                  is_device
                  exact_code_id
                  billing_item_id
                  vat
                }
              }
            }
          }
        `
      };

      this.setShowLoadingModal(true);
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res && this.checkSystem(res.inquiry.brand)) {
            this.inquiry = res.inquiry;
            this.createOptions();
          }
        })
        .finally(() => {
          this.setShowLoadingModal(false);
        });
    },
    createOptions() {
      this.options = this.inquiry.inquiry_group.map(group => {
        return {
          text: `Payment: ${group.payment_term_name},
             Duration: ${group.contract_duration},
             Total: ${(group.total_price_one_time_with_discount +
               group.total_price_per_month_with_discount *
                 group.contract_duration) /
               100}`,
          value: group.id
        };
      });
    },
    showPdf(id) {
      window.open(
        `${this.getBackendUrl}/pdf_inquiries/${id}.pdf?tmpl=0&locale=${this.getLocale}`,
        "_blank"
      );
    },
    sendDocusign(id) {
      if (!this.inquiry.docusign_sent) {
        let data = {
          query: `
            mutation InquirySendDocusign($inquiry_group_id: ID!) {
              res: InquirySendDocusign(inquiry_group_id: $inquiry_group_id)
            }
          `,
          variables: {
            inquiry_group_id: id
          }
        };

        this.simpleMutation({ data, this_ref: this }).then(() => {
          this.fetchInquiry();
        });
      }
    },
    showPayments(tab) {
      if (tab.contract_duration === 0 || tab.payment_term === 0) {
        return false;
      } else return true;
    },
    checkForExistingOrder() {
      const data = {
        query: `
          query {
            checkExistingOrderForInquiry (
              inquiry_id: ${this.$route.params.id})
          }
        `
      };

      this.setShowLoadingModal(true);
      this.simpleFetch({data, this_ref: this}).then((res) => {
        if (res) {
          this.orderExists = res.checkExistingOrderForInquiry;
        }
      }).finally(()=>{
        this.setShowLoadingModal(false)
      });
    }
  },
  computed: {
    ...mapGetters([
      "getBackendUrl",
      "getUserSystem",
      "getLocale",
      "getUserBrandPermissions"
    ]),
    isDurationSet() {
      return (
        this.newInquiryGroup !== null &&
        this.newInquiryGroup.contract_duration >= 0
      );
    },
    docusignSendEnabled() {
      return (
        this.inquiry.status == 'pending'
        && !this.inquiry.docusign_sent
        && this.getUserBrandPermissions.super_admin
      )
    }
  },
  watch: {
    "newInquiryGroup.contract_duration": function(newDuration, oldDuration) {
      if (oldDuration === undefined || oldDuration == null) return;
      this.newInquiryGroup.items = [];
      this.fetchPriceList();
    }
  },
  created() {
    if (!this.getUserBrandPermissions.inquiry_show) {
      this.$router.push({ name: "dashboard" });
    }
  },
  mounted() {
    this.fetchInquiry();
    this.checkForExistingOrder();
  }
};
</script>
